import { getUserVisibleOrDefaultExceptionMessage, upsertLocally } from '@paragon/client-tools';
import { BearingEnvelope, BearingEnvelopeType, Point2D } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { point2DEquals } from '@paragon/geometry-library';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray, ModifierKeys, notNullish } from '@paragon/utilities';

import { getBearingEnvelopeSelectableType } from '../../../library/selection/types';
import { getBearingEnvelopeUserVisibleTerm } from '../../../library/utilities/bearingEnvelopeUserTerm';
import { findNextName } from '../../../library/utilities/findNextName';
import { ensureEntryIsEntirelyVisibleToCamera } from '../../../three/ThreeJsSceneActions';
import { isInteractiveEditActive } from '../../selectors/interactiveEditingSelectors';
import {
  getActiveLayoutLevelGuidOrNull,
  getActiveLevelOrNull,
  getAllBearingEnvelopesDictionary,
  getBearingEnvelopeElevationsFromLevel,
} from '../../selectors/layoutDataSelectors';
import {
  applicationOnInsertingBearingEnvelope,
  applicationOnPickingLayoutPoint,
  clearInsertingObject,
  DEFAULT_BEAM_DEPTH,
  DEFAULT_BEARING_ENVELOPE_SETTINGS,
  DEFAULT_WALL_DEPTH,
  InsertionBearingSettings,
  updateInsertingBearingEnvelope,
} from '../../slices/applicationSlice';
import { getBearingEnvelopeInsertionSettings, layoutSlice } from '../../slices/layoutSlice';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { selectItem } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionReturn, ThunkActionVoid } from '../thunkActions';
import { autoCutRecentlyInsertedPlanes, insertRoofPlaneOnBearingEnvelope } from './roofPlaneActions';

function startInsertingBearingEnvelopeWithDefaultProperties(
  defaultProperties: Partial<InsertionBearingSettings> &
    Pick<InsertionBearingSettings, 'top' | 'bottom' | 'bearingType'>,
  basePoint?: Point2D,
): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();

    const insertionSettings = getBearingEnvelopeInsertionSettings(state.layout, defaultProperties.bearingType);

    const levelGuid = getActiveLayoutLevelGuidOrNull(state);
    if (levelGuid) dispatch(ensureLevelIsVisible(levelGuid, { exclusive: true }));

    const activeLevel = getActiveLevelOrNull(state);

    const firstBearingEnvelopeInScope =
      levelGuid == null
        ? Object.keys(state.layout.bearingEnvelopes).length === 0
        : Object.values(state.layout.bearingEnvelopes).every(
            (bearingEnvelope) => bearingEnvelope.levelGuid !== levelGuid,
          );

    const insertionData = {
      leftPoint: basePoint ?? (firstBearingEnvelopeInScope ? { x: 0, y: 0 } : undefined),

      // carefully consider the order in which "sources of default properties" are spread to ensure correct priority
      ...DEFAULT_BEARING_ENVELOPE_SETTINGS,
      ...defaultProperties,
      ...insertionSettings.defaultProperties,

      levelGuid,
    };

    // only override the previous insertion's elevations if we're syncing with the active level
    const elevationsFromLevel =
      insertionSettings.setElevationsFromLevel && activeLevel != null
        ? getBearingEnvelopeElevationsFromLevel(activeLevel, insertionData)
        : {};

    const finalInsertionData = {
      ...insertionData,
      ...elevationsFromLevel,
    };

    dispatch(
      applicationOnInsertingBearingEnvelope({
        insertionData: finalInsertionData,
      }),
    );
  };
}

export const startInsertingWall = (basePoint?: Point2D): ThunkActionVoid =>
  startInsertingBearingEnvelopeWithDefaultProperties({ bearingType: 'Wall', ...DEFAULT_WALL_DEPTH }, basePoint);

export const startInsertingBeam = (basePoint?: Point2D): ThunkActionVoid =>
  startInsertingBearingEnvelopeWithDefaultProperties({ bearingType: 'Beam', ...DEFAULT_BEAM_DEPTH }, basePoint);

/**
 * @returns a boolean to indicate whether a reset actually occurred or not.
 */
export const backUpBearingEnvelopeInsertionPointSelection = (): ThunkActionReturn<boolean> => (dispatch, getState) => {
  const state = getState();

  if (state.application.insertingObjectInfo?.type !== 'bearingEnvelope') {
    return false;
  }

  const { bearingType, leftPoint, rightPoint } = state.application.insertingObjectInfo.data;

  if (leftPoint != null && rightPoint != null) {
    dispatch(updateInsertingBearingEnvelope({ bearingType, changes: { rightPoint: undefined } }));
    dispatch(applicationOnPickingLayoutPoint({ target: 'bearing-envelope-right' }));
    return true;
  }

  if (leftPoint != null || rightPoint != null) {
    const target = leftPoint != null ? 'bearing-envelope-left' : 'bearing-envelope-right';
    dispatch(updateInsertingBearingEnvelope({ bearingType, changes: { leftPoint: undefined, rightPoint: undefined } }));
    dispatch(applicationOnPickingLayoutPoint({ target }));
    return true;
  }

  return false;
};

export type BearingEnvelopeInsertionFinalizingAction = 'finish' | 'repeat';
export type BearingEnvelopeInsertionCompletionAction = BearingEnvelopeInsertionFinalizingAction | null;

export function getBearingEnvelopeInsertionCompletionAction(
  modifiers: ModifierKeys | undefined,
  fallback: BearingEnvelopeInsertionCompletionAction,
): BearingEnvelopeInsertionCompletionAction {
  if (modifiers?.altKey) return 'repeat';
  if (modifiers?.ctrlKey) return 'finish';

  return fallback;
}

export const finishInsertingBearingEnvelope =
  (options?: { postInsertionAction?: 'repeat' }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { insertingObjectInfo } = state.application;
    if (insertingObjectInfo?.type !== 'bearingEnvelope') {
      return;
    }

    const { bearingType } = insertingObjectInfo.data;
    const insertRoofPlane = getBearingEnvelopeInsertionSettings(state.layout, bearingType).insertRoofPlane;

    const guid = crypto.randomUUID();

    const takenNames = Object.values(state.layout.bearingEnvelopes)
      .filter((e) => e.bearingType === bearingType)
      .map((e) => e.name);
    const name = findNextName(takenNames);

    const { leftPoint, rightPoint, ...otherProperties } = insertingObjectInfo.data;

    if (leftPoint == null || rightPoint == null) {
      // This action can legitimately be called when the bearing envelope has not yet been fully defined via alt+click,
      //   so it is important to simply return and not throw here.
      return;
    }

    if (point2DEquals(leftPoint, rightPoint)) {
      return;
    }

    const bearingEnvelope = { guid, name, leftPoint, rightPoint, ...otherProperties };

    dispatch(
      layoutSlice.actions.bearingEnvelopesUpserted({
        bearingEnvelopes: [bearingEnvelope],
        insertedBearingEnvelopeGuid: bearingEnvelope.guid,
      }),
    );

    if (options?.postInsertionAction !== 'repeat') {
      ensureEntryIsEntirelyVisibleToCamera({
        type: getBearingEnvelopeSelectableType(bearingEnvelope),
        item: bearingEnvelope,
      });
    }

    switch (options?.postInsertionAction) {
      case 'repeat':
        dispatch(
          layoutSlice.actions.bearingEnvelopeInsertionLockedLengthSet({
            bearingType: bearingEnvelope.bearingType,
            lockedLength: null,
          }),
        );
        dispatch(
          bearingEnvelope.bearingType === 'Beam' ? startInsertingBeam(rightPoint) : startInsertingWall(rightPoint),
        );
        break;

      case undefined:
        dispatch(clearInsertingObject());
        dispatch(selectItem(bearingEnvelope, getBearingEnvelopeSelectableType(bearingEnvelope)));

        break;
    }

    await dispatch(saveBearingEnvelope(bearingEnvelope));

    if (insertRoofPlane) {
      await dispatch(insertRoofPlaneOnBearingEnvelope(bearingEnvelope));
    }

    if (options?.postInsertionAction !== 'repeat') {
      await dispatch(autoCutRecentlyInsertedPlanes());
    }
  };

export interface BearingEnvelopeEditInstructions {
  guid: string;
  editedProperties: Partial<Omit<BearingEnvelope, 'guid'>>;
}

function errorSavingMessage(bearingType?: BearingEnvelopeType): string {
  const term = getBearingEnvelopeUserVisibleTerm(bearingType, { capitalize: false });
  return `Unable to save ${term}. Please try again or contact Paragon Customer Support.`;
}

export const editBearingEnvelopes =
  (
    changes: BearingEnvelopeEditInstructions | BearingEnvelopeEditInstructions[],
    options?: {
      createdEnvelopes?: BearingEnvelope[];
    },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    let existingEnvelopes = getAllBearingEnvelopesDictionary(state);
    if (options?.createdEnvelopes) {
      existingEnvelopes = upsertLocally(existingEnvelopes, options.createdEnvelopes);
    }

    const bearingType = forceArray(changes)
      .map(({ guid }) => existingEnvelopes[guid]?.bearingType)
      .filter(notNullish)[0];

    const editedEnvelopes = forceArray(changes).map(({ guid, editedProperties }) => {
      const originalEnvelope = existingEnvelopes[guid];

      if (originalEnvelope == null) {
        dispatch(enqueueError(errorSavingMessage(bearingType)));
        throw new Error(`Unable to find bearing envelope '${guid}' in order to apply edits.`);
      }

      let editedEnvelope = { ...originalEnvelope, ...editedProperties };

      if (point2DEquals(editedEnvelope.leftPoint, editedEnvelope.rightPoint)) {
        editedEnvelope = {
          ...editedEnvelope,
          leftPoint: originalEnvelope.leftPoint,
          rightPoint: originalEnvelope.rightPoint,
        };
      }

      return editedEnvelope;
    });

    dispatch(layoutSlice.actions.bearingEnvelopesUpserted({ bearingEnvelopes: editedEnvelopes }));

    if (state.application.openContext?.type === 'project') {
      await dispatch(saveBearingEnvelopes());
    }
  };

const saveBearingEnvelope =
  (bearingEnvelope: BearingEnvelope): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.application.openContext?.type !== 'project') return;

    const spinnerId = dispatch(
      addSpinnerMessage(
        `Saving ${getBearingEnvelopeUserVisibleTerm(bearingEnvelope.bearingType, { capitalize: false })}...`,
      ),
    );

    try {
      await designService.projects.upsertBearingEnvelope(state.application.openContext.projectGuid, bearingEnvelope);
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(error, errorSavingMessage(bearingEnvelope.bearingType))),
      );
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

// This action takes no parameters because the route used replaces all bearing envelopes.
export const saveBearingEnvelopes = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return;

  const spinnerId = dispatch(
    addSpinnerMessage(`Saving ${getBearingEnvelopeUserVisibleTerm(null, { plural: true, capitalize: false })}...`),
  );

  try {
    await designService.projects.replaceBearingEnvelopes(
      state.application.openContext.projectGuid,
      state.layout.bearingEnvelopes,
    );
  } catch (error) {
    console.error(error);
    dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, errorSavingMessage())));
    return;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const setHoveredBearingEnvelopeEdgeIfChanged =
  (guid: string, edgeAngle: number): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    const { hoveredBearingEnvelopeEdge } = state.layout;
    if (hoveredBearingEnvelopeEdge?.guid !== guid || hoveredBearingEnvelopeEdge?.edgeAngle !== edgeAngle) {
      dispatch(layoutSlice.actions.hoveredBearingEnvelopeEdgeSet({ guid, edgeAngle }));
    }
  };

export const clearHoveredBearingEnvelopeEdge = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  if (isInteractiveEditActive(state)) return;

  if (state.layout.hoveredBearingEnvelopeEdge) {
    dispatch(layoutSlice.actions.hoveredBearingEnvelopeEdgeCleared());
  }
};

export const clearHoveredBearingEnvelopeInteractiveEditHoverIfActive =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    if (state.layout.hoveredBearingEnvelopeEdge?.guid === guid) {
      dispatch(layoutSlice.actions.hoveredBearingEnvelopeEdgeCleared());
    }

    if (state.layout.hoveredBearingEnvelopeInterior?.guid === guid) {
      dispatch(layoutSlice.actions.hoveredBearingEnvelopeInteriorCleared());
    }
  };

export const setHoveredBearingEnvelopeInteriorIfChanged =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    const { hoveredBearingEnvelopeInterior } = state.layout;
    if (hoveredBearingEnvelopeInterior?.guid !== guid) {
      dispatch(layoutSlice.actions.hoveredBearingEnvelopeInteriorSet({ guid }));
    }
  };

export const clearHoveredBearingEnvelopeInterior = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  if (isInteractiveEditActive(state)) return;

  if (state.layout.hoveredBearingEnvelopeInterior) {
    dispatch(layoutSlice.actions.hoveredBearingEnvelopeInteriorCleared());
  }
};
